import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Edit3, 
  Trash2, 
  Calendar, 
  Clock, 
  Eye, 
  Share2, 
  Bookmark,
  Heart
} from 'lucide-react';
import type { NewsArticleWithRelations } from '../../types/news';

export interface NewsArticleCardProps {
  article: NewsArticleWithRelations;
  isAdmin?: boolean;
  onEdit?: () => void;
  onDelete?: (id: string) => void;
  children?: React.ReactNode;
}

export const NewsArticleCard: React.FC<NewsArticleCardProps> = ({
  article,
  isAdmin,
  onEdit,
  onDelete,
  children
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isLiked, setIsLiked] = React.useState(false);
  const navigate = useNavigate();

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLiked(!isLiked);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt || '',
        url: window.location.href
      });
    }
  };

  const handleClick = () => {
    if (isAdmin) {
      return; // Não navegar se estiver no modo admin
    }
    navigate(`/news/${article.slug}`);
  };

  const formatReadingTime = (time: string) => {
    // Remove qualquer texto adicional e mantenha apenas os números e ':'
    const timeMatch = time.match(/(\d+:)?(\d+:\d+|\d+)/);
    if (!timeMatch) return time;
    
    const timeParts = timeMatch[0].split(':');
    // Se tiver horas, converte para minutos
    if (timeParts.length === 3) {
      const [hours, minutes, seconds] = timeParts;
      const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);
      return `${totalMinutes}:${seconds}`;
    }
    // Se já estiver em minutos:segundos, retorna como está
    return timeMatch[0];
  };

  return (
    <motion.div 
      className="group relative bg-gradient-to-br from-card/90 to-card/40 rounded-xl overflow-hidden border border-white/10 hover:border-primary/20 transition-all shadow-lg hover:shadow-xl hover:shadow-primary/5"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ scale: 1.02 }}
      transition={{ 
        duration: 0.2,
        scale: {
          type: "spring",
          stiffness: 300,
          damping: 20
        }
      }}
    >
      <motion.div
        initial="initial"
        whileHover="hover"
        animate="initial"
        variants={{
          initial: { scale: 1 },
          hover: { scale: 1.05 }
        }}
        className="relative h-48 overflow-hidden"
      >
        <motion.img
          src={article.cover_image}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover"
          variants={{
            initial: {
              scale: 1,
              filter: "brightness(1)",
              transform: "translateY(0)"
            },
            hover: {
              scale: 1.1,
              filter: "brightness(0.7)",
              transform: "translateY(-5px)"
            }
          }}
          transition={{
            duration: 0.4,
            ease: "easeOut"
          }}
        />
        <motion.div
          variants={{
            initial: { opacity: 0 },
            hover: { opacity: 1 }
          }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent"
        />
      </motion.div>

      <div className="relative p-6 space-y-4 bg-gradient-to-b from-transparent via-card/5 to-card/20">
        {/* Categoria e Título */}
        <div>
          <motion.span 
            className="inline-block px-3 py-1 bg-primary/10 text-primary rounded-full text-sm mb-2 border border-primary/20"
            whileHover={{ scale: 1.05 }}
          >
            {article.category?.name}
          </motion.span>
          <h3 className="text-lg font-semibold mb-2 line-clamp-1 group-hover:text-primary transition-colors">
            {article.title}
          </h3>
          <p className="text-sm text-gray-400 line-clamp-2">
            {article.excerpt}
          </p>
        </div>

        {/* Estatísticas e Data */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Clock className="w-4 h-4 text-primary/70 shrink-0" />
              <span className="truncate">{formatReadingTime(article.reading_time || '')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Eye className="w-4 h-4 text-primary/70 shrink-0" />
              <span className="truncate">{article.views_count} views</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Calendar className="w-4 h-4 text-primary/70 shrink-0" />
            <span className="truncate">{new Date(article.published_at || '').toLocaleDateString()}</span>
          </div>
        </div>

        {/* Ações */}
        {isAdmin && (
          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            {children || (
              <>
                {onEdit && (
                  <button
                    onClick={onEdit}
                    className="flex items-center gap-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-all hover:scale-105 hover:shadow-lg hover:shadow-primary/10"
                    title="Editar notícia"
                  >
                    <Edit3 className="w-4 h-4" />
                    <span className="font-medium">Editar</span>
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => onDelete(article.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-destructive/30 hover:bg-destructive text-destructive-foreground/90 hover:text-destructive-foreground rounded-lg transition-all hover:scale-105 hover:shadow-lg hover:shadow-destructive/10"
                    title="Apagar notícia"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="font-medium">Apagar</span>
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Efeito de overlay no hover */}
      <motion.div 
        className="absolute inset-0 border-2 border-primary/0 rounded-xl pointer-events-none"
        variants={{
          initial: { borderColor: "rgba(0, 149, 255, 0)" },
          hover: { borderColor: "rgba(0, 149, 255, 0.2)" }
        }}
        transition={{ duration: 0.3 }}
      />
    </motion.div>
  );
};